// BackupPanel.jsx — place in src/ (next to App.js)
import React, { useRef, useState, useEffect } from 'react';

export default function BackupPanel({ API }) {
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stored, setStored] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const base = () => process.env.REACT_APP_API_URL.replace(/\/+$/, '');
  const token = () => localStorage.getItem('cms_token');

  // Load the 7 stored auto-backups whenever the dialog opens
  useEffect(() => {
    if (!open) return;
    setLoadingList(true);
    API.get('/api/backup/list')
      .then(r => setStored(r.data.backups || []))
      .catch(() => setStored([]))
      .finally(() => setLoadingList(false));
  }, [open]); // eslint-disable-line

  const fmtSize = (b) => {
    if (b == null) return '';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
    return (b / 1024 / 1024).toFixed(1) + ' MB';
  };
  const fmtDate = (d) => {
    try { return new Date(d).toLocaleString(); } catch { return d; }
  };

  // Fresh, on-demand backup (top-right)
  const downloadFresh = () => {
    window.location.href = `${base()}/api/backup?token=${encodeURIComponent(token())}`;
  };

  // Download one stored backup
  const downloadStored = (key) => {
    window.location.href = `${base()}/api/backup/download?key=${encodeURIComponent(key)}&token=${encodeURIComponent(token())}`;
  };

  // Restore one stored backup
  const restoreStored = async (item) => {
    const ok = window.confirm(
      `Restore the backup from ${fmtDate(item.lastModified)}?\n\n` +
      'This REPLACES all current CMS content and media with that snapshot.\n' +
      'A safety copy of the current data is saved on the server first.'
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await API.post('/api/backup/restore-stored', { key: item.key });
      alert('Restored backup from ' + fmtDate(res.data.restoredFrom));
      window.location.reload();
    } catch (err) {
      alert('Restore failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
    }
  };

  // Restore from an uploaded file (top-right)
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ok = window.confirm(
      'This will REPLACE all current CMS content with this backup.\n' +
      'A safety snapshot is saved on the server first.\n\nContinue?'
    );
    if (!ok) { e.target.value = ''; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('backup', file);
      const res = await fetch(`${base()}/api/backup/restore`, {
        method: 'POST',
        headers: token() ? { Authorization: `Bearer ${token()}` } : {},
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || ('Server returned ' + res.status));
      alert('Restored backup from ' + fmtDate(data.restoredFrom));
      window.location.reload();
    } catch (err) {
      alert('Restore failed: ' + err.message);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  return (
    <>
      <button className="sidebar-btn" onClick={() => setOpen(true)} disabled={busy}>
        {'\u{1F5C4}'} Backup
      </button>

      {open && (
        <div style={S.overlay} onClick={() => !busy && setOpen(false)}>
          <div style={S.dialog} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={S.header}>
              <div>
                <div style={S.title}>Backup &amp; Restore</div>
                <div style={S.subtitle}>Daily automatic backups are kept for 7 days.</div>
              </div>
              <div style={S.topActions}>
                <button style={S.topBtn} onClick={downloadFresh} disabled={busy} title="Generate & download a fresh backup now">
                  {'\u2b07'} Download
                </button>
                <button style={S.topBtnGhost} onClick={() => fileRef.current?.click()} disabled={busy} title="Restore from a backup file on your computer">
                  {'\u2b06'} Upload
                </button>
              </div>
            </div>

            <div style={S.divider} />

            {/* Stored backups list */}
            <div style={S.listLabel}>Automatic backups (last 7 days)</div>

            <div style={S.list}>
              {loadingList && <div style={S.muted}>Loading…</div>}
              {!loadingList && stored.length === 0 && (
                <div style={S.muted}>No automatic backups yet. The first one runs tonight at 2 AM, or use “Download” above to make one now.</div>
              )}
              {!loadingList && stored.map((item) => (
                <div key={item.key} style={S.row}>
                  <div style={S.rowInfo}>
                    <div style={S.rowDate}>{fmtDate(item.lastModified)}</div>
                    <div style={S.rowMeta}>{fmtSize(item.size)}</div>
                  </div>
                  <div style={S.rowBtns}>
                    <button style={S.rowRestore} onClick={() => restoreStored(item)} disabled={busy}>
                      Restore
                    </button>
                    <button style={S.rowDownload} onClick={() => downloadStored(item.key)} disabled={busy}>
                      {'\u2b07'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button style={S.close} onClick={() => setOpen(false)} disabled={busy}>
              {busy ? 'Working…' : 'Close'}
            </button>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept=".zip" hidden onChange={onFile} />
    </>
  );
}

// Dark theme, matched to the studio
const S = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(3,7,18,0.72)',
    backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  dialog: {
    background: '#0f172a', color: '#e2e8f0',
    border: '1px solid #1e293b', borderRadius: 14, padding: 22,
    width: 460, maxWidth: '92vw', maxHeight: '82vh',
    boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  title: { fontSize: 17, fontWeight: 700, color: '#f8fafc' },
  subtitle: { fontSize: 12, color: '#94a3b8', marginTop: 3 },
  topActions: { display: 'flex', gap: 8, flexShrink: 0 },
  topBtn: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #2563eb',
    background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  topBtnGhost: {
    padding: '8px 12px', borderRadius: 8, border: '1px solid #334155',
    background: 'transparent', color: '#cbd5e1', fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  divider: { height: 1, background: '#1e293b' },
  listLabel: { fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.4 },
  list: { display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', paddingRight: 2 },
  muted: { fontSize: 13, color: '#64748b', padding: '10px 2px', lineHeight: 1.5 },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', borderRadius: 10,
    background: '#111c33', border: '1px solid #1e293b',
  },
  rowInfo: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
  rowDate: { fontSize: 13, color: '#e2e8f0', fontWeight: 600 },
  rowMeta: { fontSize: 11, color: '#64748b' },
  rowBtns: { display: 'flex', gap: 6, flexShrink: 0 },
  rowRestore: {
    padding: '6px 12px', borderRadius: 7, border: '1px solid #2563eb',
    background: 'transparent', color: '#60a5fa', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  rowDownload: {
    padding: '6px 10px', borderRadius: 7, border: '1px solid #334155',
    background: 'transparent', color: '#cbd5e1', fontSize: 12, cursor: 'pointer',
  },
  close: {
    marginTop: 2, padding: '9px 14px', borderRadius: 8, border: '1px solid #334155',
    background: 'transparent', color: '#94a3b8', fontSize: 13, cursor: 'pointer',
  },
};